import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Paper,
    Alert,
    Slide,
    Divider
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

interface PasswordResetDialogProps {
    open: boolean;
    userName: string;
    userEmail?: string;
    newPassword: string;
    onClose: () => void;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function PasswordResetDialog({
    open,
    userName,
    userEmail,
    newPassword,
    onClose
}: PasswordResetDialogProps) {
    const [copied, setCopied] = useState(false);

    const handleCopyPassword = async () => {
        try {
            await navigator.clipboard.writeText(newPassword);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch (error) {
            console.error('Failed to copy password:', error);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            sx={{ zIndex: 10000000 }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'success.main',
                color: 'white'
            }}>
                <CheckCircleIcon sx={{ mr: 1.5, fontSize: 32 }} />
                <Typography variant="h6" component="div">
                    Senha Redefinida com Sucesso
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    A senha foi redefinida. Compartilhe as informações abaixo com o usuário através de um canal seguro.
                </Alert>

                {/* User Information */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 2,
                        mb: 3
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 2, fontWeight: 600 }}
                    >
                        Informações do Usuário
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <PersonIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Nome
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {userName}
                            </Typography>
                        </Box>
                    </Box>

                    {userEmail && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EmailIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Email
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {userEmail}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>

                {/* Password Section */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        bgcolor: 'primary.50',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <VpnKeyIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                        <Typography
                            variant="subtitle2"
                            color="primary.main"
                            sx={{ fontWeight: 600 }}
                        >
                            Nova Senha Temporária
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: 'white',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: 'monospace',
                                letterSpacing: '0.5px',
                                color: 'text.primary',
                                fontWeight: 600
                            }}
                        >
                            {newPassword}
                        </Typography>

                        <IconButton
                            onClick={handleCopyPassword}
                            color={copied ? "success" : "primary"}
                            sx={{
                                ml: 2,
                                '&:hover': {
                                    bgcolor: 'primary.50'
                                }
                            }}
                            title="Copiar senha"
                        >
                            <ContentCopyIcon />
                        </IconButton>
                    </Box>

                    {copied && (
                        <Alert
                            severity="success"
                            sx={{ mt: 2 }}
                            icon={<CheckCircleIcon fontSize="inherit" />}
                        >
                            Senha copiada para a área de transferência!
                        </Alert>
                    )}
                </Paper>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 2, textAlign: 'center' }}
                >
                    O usuário deverá alterar esta senha no primeiro acesso
                </Typography>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2.5 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    size="large"
                    fullWidth
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
